/**
 * useTravelTodos — per-trip todos (Packing list, etc.)
 *
 * Scoped by optional `category` so the packing panel can subscribe to just
 * the packing list while a future "visa paperwork" tab can subscribe to
 * its own category. The mutations invalidate both the scoped and the
 * un-scoped list keys so any other panel listening stays fresh.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import type { TravelTodoResponse } from '../api';
import { queryKeys } from '@/lib/query-keys';
import { useTravelFeature } from './useTravelUserProfile';

export function useTravelTodos(tripId: string | undefined, category?: string) {
  const queryClient = useQueryClient();
  const enabled = !!tripId;

  const listKey = tripId ? queryKeys.travel.todos(tripId, category) : ['travel', 'todos', 'none'];

  const listQuery = useQuery({
    enabled,
    queryKey: listKey,
    queryFn: () => travelApi.listTravelTodos(tripId!, category ? { category } : undefined),
  });

  const invalidate = () => {
    if (!tripId) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.travel.todos(tripId, category) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.travel.todos(tripId) });
  };

  const createMut = useMutation({
    mutationFn: (input: { title: string; notes?: string | null; category?: string }) =>
      travelApi.createTravelTodo(tripId!, input),
    onSuccess: invalidate,
  });

  const updateMut = useMutation({
    mutationFn: (vars: { todoId: string; input: Record<string, unknown> }) =>
      travelApi.updateTravelTodo(vars.todoId, vars.input),
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (todoId: string) => travelApi.deleteTravelTodo(todoId),
    onSuccess: invalidate,
  });

  return {
    todos: (listQuery.data?.todos ?? []) as TravelTodoResponse[],
    loading: listQuery.isLoading,
    error: listQuery.error instanceof Error ? listQuery.error.message : null,
    refresh: () => listQuery.refetch(),
    createTodo: (input: { title: string; notes?: string | null; category?: string }) =>
      createMut.mutateAsync(input),
    toggleTodo: (todoId: string, completed: boolean) =>
      updateMut.mutateAsync({ todoId, input: { completed } }),
    updateTodo: (todoId: string, input: Record<string, unknown>) =>
      updateMut.mutateAsync({ todoId, input }),
    deleteTodo: (todoId: string) => deleteMut.mutateAsync(todoId),
    isCreating: createMut.isPending,
    isUpdating: updateMut.isPending,
  };
}

/**
 * Kicks off the AI-driven packing list generator and invalidates the todos
 * cache so the panel picks up the new rows. Separate from the CRUD hook
 * above so pages that don't need the generator don't pay the bundle size
 * of the AI call surface.
 */
export function useGeneratePackingList(tripId: string | undefined) {
  const queryClient = useQueryClient();
  const feature = useTravelFeature('packing_list');

  const generateMut = useMutation({
    mutationFn: () => travelApi.generateTravelPackingList(tripId!),
    onSuccess: () => {
      if (!tripId) return;
      void queryClient.invalidateQueries({
        queryKey: queryKeys.travel.todos(tripId),
      });
    },
  });

  return {
    featureEnabled: feature.enabled,
    generate: () => generateMut.mutateAsync(),
    isGenerating: generateMut.isPending,
    error: generateMut.error instanceof Error ? generateMut.error.message : null,
  };
}
